/**
 * Promotion Service - Firestore operations for promotions/sales management
 *
 * Promotions are sales campaigns with discount details that can be
 * incorporated into content briefs. Unlike events, promotions have
 * specific discount types, values, codes, and date ranges.
 */

import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const PROMOTIONS_COLLECTION = 'promotions';

/**
 * Promotion types
 */
export const PROMOTION_TYPES = {
    percentage: { label: 'Percentage Off', example: '20% off' },
    fixed: { label: 'Fixed Amount Off', example: '$10 off' },
    bogo: { label: 'Buy One Get One', example: 'BOGO 50% off' },
    bundle: { label: 'Bundle Deal', example: 'Buy 2 get 1 free' },
    free_shipping: { label: 'Free Shipping', example: 'Free shipping over $50' }
};

/**
 * Urgency levels for promotional content
 */
export const URGENCY_LEVELS = {
    low: { label: 'Low', description: 'Subtle mention, no pressure' },
    medium: { label: 'Medium', description: 'Clear urgency, limited time' },
    high: { label: 'High', description: 'Strong urgency, scarcity messaging' }
};

/**
 * Default promotion structure
 */
export const DEFAULT_PROMOTION = {
    name: '',
    type: 'percentage',
    discountValue: '',
    discountCode: '',

    isOngoing: false, // If true, no end date - always active
    startDate: '',
    endDate: '',

    applicableProducts: [], // empty = all products (for discount applicability)
    linkedProducts: [],     // Product IDs to feature in content generation
    description: '',

    contentGuidelines: {
        urgencyLevel: 'medium',
        emphasizeScarcity: false,
        legalDisclaimer: ''
    },

    isActive: true
};

/**
 * Create a new promotion
 */
export async function createPromotion(brandId, userId, promotionData) {
    if (!brandId || !userId) {
        throw new Error('Brand ID and User ID are required');
    }

    const promotion = {
        ...DEFAULT_PROMOTION,
        ...promotionData,
        brandId,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, PROMOTIONS_COLLECTION), promotion);

    return {
        id: docRef.id,
        ...promotion
    };
}

/**
 * Get a single promotion by ID
 */
export async function getPromotion(promotionId) {
    const docRef = doc(db, PROMOTIONS_COLLECTION, promotionId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return {
        id: docSnap.id,
        ...docSnap.data()
    };
}

/**
 * Get all promotions for a brand
 */
export async function getPromotionsByBrand(brandId) {
    if (!brandId) {
        throw new Error('Brand ID is required');
    }

    const q = query(
        collection(db, PROMOTIONS_COLLECTION),
        where('brandId', '==', brandId),
        orderBy('startDate', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

/**
 * Get promotions active during a date range
 * Returns promotions where their date range overlaps with the provided range
 * Ongoing promotions are always included
 */
export async function getActivePromotions(brandId, startDate, endDate) {
    if (!brandId) {
        throw new Error('Brand ID is required');
    }

    // Get all promotions for the brand
    const promotions = await getPromotionsByBrand(brandId);

    // Filter to those that overlap with the date range (or are ongoing)
    const start = new Date(startDate);
    const end = new Date(endDate);

    return promotions.filter(promo => {
        if (!promo.isActive) return false;

        // Ongoing promotions are always included
        if (promo.isOngoing) return true;

        const promoStart = new Date(promo.startDate);
        const promoEnd = new Date(promo.endDate);

        // Check for overlap: promo starts before range ends AND promo ends after range starts
        return promoStart <= end && promoEnd >= start;
    });
}

/**
 * Update a promotion
 */
export async function updatePromotion(promotionId, updates) {
    const docRef = doc(db, PROMOTIONS_COLLECTION, promotionId);

    await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
    });

    return getPromotion(promotionId);
}

/**
 * Delete a promotion
 */
export async function deletePromotion(promotionId) {
    const docRef = doc(db, PROMOTIONS_COLLECTION, promotionId);
    await deleteDoc(docRef);
}

/**
 * Get promotion status based on current date
 */
export function getPromotionStatus(promotion) {
    if (!promotion.isActive) {
        return { status: 'inactive', label: 'Inactive', color: 'var(--color-text-tertiary)' };
    }

    // Ongoing promotions are always active
    if (promotion.isOngoing) {
        return { status: 'ongoing', label: 'Ongoing', color: 'var(--success-600)' };
    }

    const now = new Date();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);

    // Set times to start/end of day for accurate comparison
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    now.setHours(12, 0, 0, 0); // Middle of current day

    if (now < start) {
        return { status: 'upcoming', label: 'Upcoming', color: 'var(--info-600)' };
    }

    if (now > end) {
        return { status: 'ended', label: 'Ended', color: 'var(--color-text-tertiary)' };
    }

    return { status: 'active', label: 'Active', color: 'var(--success-600)' };
}

/**
 * Format discount value for display
 */
export function formatDiscountValue(promotion) {
    switch (promotion.type) {
        case 'percentage':
            return `${promotion.discountValue}% off`;
        case 'fixed':
            return `$${promotion.discountValue} off`;
        case 'bogo':
            return promotion.discountValue || 'Buy One Get One';
        case 'bundle':
            return promotion.discountValue || 'Bundle Deal';
        case 'free_shipping':
            return promotion.discountValue || 'Free Shipping';
        default:
            return promotion.discountValue;
    }
}

/**
 * Build promotion context for AI prompts
 * @param {Object} promotion - The promotion object
 * @param {Array} products - Optional array of all products (used to resolve linkedProducts)
 */
export function buildPromotionContext(promotion, products = []) {
    if (!promotion) return '';

    const discountDisplay = formatDiscountValue(promotion);
    const validityText = promotion.isOngoing
        ? 'Ongoing promotion (always available)'
        : `Valid: ${promotion.startDate} to ${promotion.endDate}`;

    // Build linked products context if products are linked
    let linkedProductsContext = '';
    if (promotion.linkedProducts?.length > 0 && products.length > 0) {
        const linkedProductDetails = products.filter(p =>
            promotion.linkedProducts.includes(p.id)
        );
        if (linkedProductDetails.length > 0) {
            linkedProductsContext = `
### LINKED PRODUCTS TO FEATURE (REQUIRED):
${linkedProductDetails.map(p => {
    const details = [`**${p.name}**`];
    if (p.price) details.push(`Price: ${p.currency || '$'}${p.price}`);
    if (p.description) details.push(`Description: ${p.description}`);
    if (p.features?.length > 0) details.push(`Features: ${p.features.slice(0, 3).join(', ')}`);
    return `- ${details.join(' | ')}`;
}).join('\n')}

**CRITICAL**: Content for this promotion MUST reference one or more of these linked products by name.
Do NOT reference products outside this list for promotional content.`;
        }
    }

    return `
## ACTIVE PROMOTION TO FEATURE
Name: ${promotion.name}
Type: ${PROMOTION_TYPES[promotion.type]?.label || promotion.type}
Discount: ${discountDisplay}
${promotion.discountCode ? `Promo Code: ${promotion.discountCode}` : ''}
${validityText}
${promotion.description ? `Description: ${promotion.description}` : ''}

### Content Guidelines for this Promotion:
- Urgency Level: ${URGENCY_LEVELS[promotion.contentGuidelines?.urgencyLevel]?.label || 'Medium'}
${promotion.isOngoing ? '- This is an ONGOING promotion - do not use time-limited urgency messaging' : ''}
${promotion.contentGuidelines?.emphasizeScarcity ? '- EMPHASIZE SCARCITY (limited stock, limited time)' : ''}
${promotion.contentGuidelines?.legalDisclaimer ? `- Legal Disclaimer to include: "${promotion.contentGuidelines.legalDisclaimer}"` : ''}
${promotion.applicableProducts?.length > 0 ? `- Applicable Products: ${promotion.applicableProducts.join(', ')}` : '- Applies to: All products'}
${linkedProductsContext}
`;
}
